'use client';

import React, { useCallback } from 'react';
import { ANALYTICS_EVENT_NAMES, classNames } from '../../../utils';
import { IconCta } from '../../Cta/IconCta';
import styles from './CarouselPaginationControls.module.scss';
import type { BasePaginationControlsProps } from '../PaginationControls/types';
import { BaseAnalyticsProps } from '../../../types';
import { analyticsService } from '../../../services/AnalyticsService';

export interface CarouselPaginationControlsProps extends BasePaginationControlsProps {
  /** Total number of pagination dots */
  totalDots: number;
  /** Function to check if a dot should be active */
  isDotActive: (index: number) => boolean;
  /** Function to go to specific index */
  goToIndex: (index: number) => void;
  /** Optional custom class name for active pagination dots */
  activeDotClassName?: string;
  /** Custom aria label for previous button */
  previousAriaLabel?: string;
  /** Custom aria label for next button */
  nextAriaLabel?: string;
  /** Unique identifier for this carousel instance to prevent key conflicts */
  carouselId?: string;
  /** Analytics config for pagination controls */
  analyticsConfig?: BaseAnalyticsProps;
  /** Current carousel index (0-based) for analytics tracking */
  currentIndex?: number;
  /** Total items in the carousel for analytics tracking */
  totalItems?: number;
  /** Right-to-left layout; flips control order and direction */
  rtl?: boolean;
}

export const CarouselPaginationControls: React.FC<CarouselPaginationControlsProps> = ({
  canGoPrevious,
  canGoNext,
  goToPrevious,
  goToNext,
  totalDots,
  isDotActive,
  goToIndex,
  className,
  activeDotClassName,
  previousAriaLabel = 'Previous',
  nextAriaLabel = 'Next',
  carouselId,
  analyticsConfig,
  currentIndex = 0,
  totalItems = 0,
  rtl = false,
}) => {
  const fallbackId = React.useId();
  const uniqueId = carouselId || fallbackId;
  const shouldShowControls = canGoPrevious || canGoNext;

  // Enhanced click handlers with dynamic analytics tracking at click time
  const handlePrevious = useCallback(() => {
    if (analyticsConfig) {
      const nextIndex = currentIndex - 1;

      // Track with current (live) runtime data at click time
      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.CAROUSEL_NAVIGATE,
        module_name: analyticsConfig.moduleName,
        component_family: analyticsConfig.componentFamily,
        component_type: analyticsConfig.componentType,
        section_header: analyticsConfig.sectionHeader,
        navigation_type: 'previous',
        current_index: nextIndex + 1, // 1-based
        previous_index: currentIndex + 1, // 1-based
        total_items: totalItems,
        total_dots: totalDots,
      } as any);
    }

    goToPrevious();
  }, [analyticsConfig, currentIndex, totalItems, totalDots, goToPrevious]);

  const handleNext = useCallback(() => {
    if (analyticsConfig) {
      const nextIndex = currentIndex + 1;

      // Track with current (live) runtime data at click time
      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.CAROUSEL_NAVIGATE,
        module_name: analyticsConfig.moduleName,
        component_family: analyticsConfig.componentFamily,
        component_type: analyticsConfig.componentType,
        section_header: analyticsConfig.sectionHeader,
        navigation_type: 'next',
        current_index: nextIndex + 1, // 1-based
        previous_index: currentIndex + 1, // 1-based
        total_items: totalItems,
        total_dots: totalDots,
      } as any);
    }

    goToNext();
  }, [analyticsConfig, currentIndex, totalItems, totalDots, goToNext]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (analyticsConfig) {
        // Track with current (live) runtime data at click time
        analyticsService.trackEvent({
          event: ANALYTICS_EVENT_NAMES.CAROUSEL_NAVIGATE,
          module_name: analyticsConfig.moduleName,
          component_family: analyticsConfig.componentFamily,
          component_type: analyticsConfig.componentType,
          section_header: analyticsConfig.sectionHeader,
          navigation_type: 'dot',
          current_index: index + 1, // 1-based
          previous_index: currentIndex + 1, // 1-based
          total_items: totalItems,
          total_dots: totalDots,
        } as any);
      }
      goToIndex(index);
    },
    [analyticsConfig, currentIndex, totalItems, totalDots, goToIndex]
  );

  if (!shouldShowControls) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles['carousel-pagination-controls'],
        className,
        totalDots >= 8 && styles['carousel-pagination-controls--small']
      )}
      dir={rtl ? 'rtl' : undefined}
    >
      {/* Previous Button */}
      <IconCta
        icon="chevron_right_thin"
        className={styles['carousel-pagination-controls__nav-button']}
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        ariaLabel={previousAriaLabel}
        size="large"
        noBorder={true}
        rotate={rtl ? 0 : 180}
      />

      {/* Pagination Dots */}
      {totalDots > 1 && (
        <div className={classNames(styles['carousel-pagination-controls__pagination'])}>
          {Array.from({ length: totalDots }, (_, index) => (
            <button
              type="button"
              key={`${uniqueId}-dot-${index}`}
              className={classNames(
                styles['carousel-pagination-controls__dot'],
                isDotActive(index) &&
                  classNames(
                    styles['carousel-pagination-controls__dot--active'],
                    activeDotClassName
                  )
              )}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Next Button */}
      <IconCta
        icon="chevron_right_thin"
        className={styles['carousel-pagination-controls__nav-button']}
        onClick={handleNext}
        disabled={!canGoNext}
        ariaLabel={nextAriaLabel}
        size="large"
        noBorder={true}
        rotate={rtl ? 180 : 0}
      />
    </div>
  );
};
